import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';

import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/map'

import { ConfirmationService } from 'primeng/primeng';
import { MessageService } from 'primeng/components/common/messageservice';

import { ApiService, getResponse, updateResponse, getSingleResponse, deleteResponse } from '../api.service';
import { Order, OrderItem, OrderDocument, SelectItem, OrderLog } from '@models';

@Injectable()
export class OrderService extends ApiService<Order> {

    protected apiGroup: string = 'shop';
    protected apiName: string = 'order';

    constructor(
        public http: HttpClient,
        public translate: TranslateService,
        public confirmationService: ConfirmationService,
        public messageService: MessageService
    ) {
        super(http, translate, confirmationService, messageService);
    }

    /**
     * Get order totals
     * 
     * @param order_id 
     */
    getTotals(order_id: number): Observable<getSingleResponse<orderTotals>> {
		return this.http.get<getSingleResponse<orderTotals>>(
			this.getBaseUrl(order_id.toString(), 'totals')
		).retry(this.retries);
    }

    /**
     * Get all items
     * 
     * @param order_id Order ID
     */
	getItems(order_id: number): Observable<getResponse<OrderItem>> {
		return this.http.get<getResponse<OrderItem>>(            
			this.getBaseUrl(order_id.toString(), 'items')
		).retry(this.retries);
    }
    
    /**
     * Add product to order
     * 
     * @param order_id Order ID
     * @param product_id OrderItem ID
     */
    addItem(order_id: number, product_stock_item_id: number): Observable<deleteResponse> {
        return this.http.post<deleteResponse>(
            this.getBaseUrl(order_id.toString(), ['items', 'add', product_stock_item_id.toString()]),
            {}
        );
    }

    /**
     * Update order item
     * 
     * @param order_id Order ID
     * @param item_id OrderItem ID
     * @param field field to update
     * @param value new field value
     */
    updateItem(order_id: number, item_id: number, field: string, value: any): Observable<updateItemResponse> {
        return this.http.post<updateItemResponse>(
            this.getBaseUrl(order_id.toString(), ['items', item_id.toString(), 'update']),
            {
                field: field,
                value: value
            }
        )
    }
    
    /**
     * Remove order item
     * 
     * @param order_id Order ID
     * @param item_id OrderItem ID
     */
    removeItem(order_id: number, item_id: number): Observable<deleteResponse> {
        return this.http.delete<deleteResponse>(
			this.getBaseUrl(order_id.toString(), ['items', item_id.toString()]),
        );
    }
    
    /**
     * Retour order item
     * 
     * @param order_id Order ID
     * @param item_id OrderItem ID
     */
    retourItem(order_id: number, item_id: number): Observable<deleteResponse> {
        return this.http.post<deleteResponse>(
            this.getBaseUrl(order_id.toString(), ['items', item_id.toString(), 'retour']),
            {}
        );
    }

    /**
     * Update order data
     * 
     * @param order_id Order ID
     * @param data Data object
     */
    updateData(order_id: number, data: any): Observable<updateDataResponse> {
        return this.http.post<updateDataResponse>(
            this.getBaseUrl(order_id.toString(), 'data'),
            data
        )
    }
    
    /**
     * Status controller
     */

    /**
     * Update Order Status
     * 
     * @param order_id Order ID
     * @param status New status
     */
    updateStatus(order_id: number, status: string, data: any = {}): Observable<updateStatusResponse> {
        return this.http.post<updateStatusResponse>(
            this.getBaseUrl(order_id.toString(), ['status', status]),
            data
        );
    }

    /**
     * Document controller
     */

    /**
     * Get all order documents
     * 
     * @param order_id OrderID
     */
    getAllDocuments(order_id: number): Observable<getResponse<OrderDocument>> {
        return this.http.get<getResponse<OrderDocument>>(
            this.getBaseUrl(order_id.toString(), 'documents')
        ).retry(this.retries);
    }

    /**
     * Create new document
     * 
     * @param order_id OrderID
     * @param type Document type
     */
    createDocument(order_id: number, type: string): Observable<createDocumentResponse> {
        return this.http.post<createDocumentResponse>(
            this.getBaseUrl(order_id.toString(), ['documents', type]),
            {}
        );
    }

    /**
     * Log controller
     */

    /**
     * Get all order logs
     * 
     * @param order_id OrderID
     */
    getAllLogs(order_id: number, page: number): Observable<getResponse<OrderLog>> {
        return this.http.get<getResponse<OrderLog>>(
            this.getBaseUrl(order_id.toString(), ['logs', page.toString()])
        ).retry(this.retries);
    }

    /**
     * selectItemArray
     */
    selectItemArray(): SelectItem[] {
        let array: SelectItem[] = [];

        for(var item of this.items) {
            array.push({
                label: item.order_number.toString(),
                value: item.id
            })
        }

        return array;
    }

    /**
     * loadItems
     * 
     * @param reload 
     */
    loadItems(reload: boolean = false): Observable<Array<Order>> {

        if(!reload && this.items.length > 0) {
            return Observable.create(observer => {
                observer.next(this.items);
                observer.complete();
            });
        }
        return this.getAll({
            fields: [
                'id', 'name'
            ]
        }).map(data => {
            this.items = data.data;
            
            return this.items;
        });
    }
}

export interface updateItemResponse extends updateResponse<any> { }

export interface updateDataResponse extends updateResponse<any> { }

export interface updateStatusResponse extends updateResponse<any> {
    status: string;
}

export interface createDocumentResponse extends updateResponse<any> {
    document: OrderDocument;
}

interface orderTotals {
    total_sum_incl: number;
    total_sum_excl: number;
    total_sum_tax: number;
    tax_totals: number;
}



// WEBPACK FOOTER //
// /var/www/vhosts/vinologiwijnen.nl/httpdocs/crm/resources/assets/src/app/shared/services/shop/order.service.ts